import productFragment from "../fragments/product";
import imageFragment from "../fragments/image";

export const getProductQuery = /* GraphQL */ `
  query getProduct($handle: String!) {
    product(handle: $handle) {
      ...product
    }
  }
  ${productFragment}
`;

export const getProductsQuery = /* GraphQL */ `
  query getProducts(
    $sortKey: ProductSortKeys
    $reverse: Boolean
    $query: String
  ) {
    products(sortKey: $sortKey, reverse: $reverse, query: $query, first: 100) {
      edges {
        node {
          ...product
        }
      }
    }
  }
  ${productFragment}
`;

export const getCollectionProductsQuery = /* GraphQL */ `
  query getCollectionProducts(
    $handle: String!
    $sortKey: ProductCollectionSortKeys
    $reverse: Boolean
  ) {
    collection(handle: $handle) {
      products(sortKey: $sortKey, reverse: $reverse, first: 100) {
        edges {
          node {
            ...product
          }
        }
      }
    }
  }
  ${productFragment}
`;

// Store cards only need pricing, color swatches, and their two card images.
// Keep this separate from the full product fragment so the /store RSC payload
// does not contain product-page media and metafields for every product.
export const getStoreCollectionProductsQuery = /* GraphQL */ `
  query getStoreCollectionProducts(
    $handle: String!
    $sortKey: ProductCollectionSortKeys
  ) {
    collection(handle: $handle) {
      products(sortKey: $sortKey, first: 100) {
        edges {
          node {
            id
            handle
            title
            tags
            featuredImage {
              ...image
            }
            images(first: 1) {
              edges {
                node {
                  ...image
                }
              }
            }
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            metafields(
              identifiers: [
                { namespace: "custom", key: "product_popularity_tag" }
              ]
            ) {
              namespace
              key
              type
              value
            }
            variants(first: 100) {
              edges {
                node {
                  id
                  title
                  sku
                  availableForSale
                  selectedOptions {
                    name
                    value
                  }
                  price {
                    amount
                    currencyCode
                  }
                  metafields(
                    identifiers: [
                      { namespace: "custom", key: "media" }
                      { namespace: "custom", key: "primary_color" }
                      { namespace: "custom", key: "accent_color" }
                      { namespace: "custom", key: "store_media_card_new" }
                    ]
                  ) {
                    namespace
                    key
                    type
                    value
                    references(first: 5) {
                      edges {
                        node {
                          __typename
                          ... on MediaImage {
                            image {
                              ...image
                            }
                          }
                          ... on Video {
                            id
                            sources {
                              url
                              mimeType
                              height
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${imageFragment}
`;

// Navigation needs only card/link data. Keep this separate from the full
// product query so opening any page does not download product-page metafields.
export const getNavigationCollectionProductsQuery = /* GraphQL */ `
  query getNavigationCollectionProducts(
    $handle: String!
    $sortKey: ProductCollectionSortKeys
  ) {
    collection(handle: $handle) {
      products(sortKey: $sortKey, first: 20) {
        edges {
          node {
            id
            handle
            title
            tags
            featuredImage {
              ...image
            }
            images(first: 1) {
              edges {
                node {
                  ...image
                }
              }
            }
            variants(first: 1) {
              edges {
                node {
                  id
                }
              }
            }
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
              maxVariantPrice {
                amount
                currencyCode
              }
            }
          }
        }
      }
    }
  }
  ${imageFragment}
`;

export const getRevolveCollectionVariantsQuery = /* GraphQL */ `
  query getRevolveCollectionVariants($handle: String!) {
    collection(handle: $handle) {
      products(first: 100, sortKey: MANUAL) {
        edges {
          node {
            id
            handle
            title
            tags
            featuredImage {
              ...image
            }
            images(first: 5) {
              edges {
                node {
                  ...image
                }
              }
            }
            variants(first: 100) {
              edges {
                node {
                  id
                  title
                  sku
                  availableForSale
                  selectedOptions {
                    name
                    value
                  }
                  price {
                    amount
                    currencyCode
                  }
                  featuredImage: image {
                    ...image
                  }
                  metafields(
                    identifiers: [
                      { namespace: "custom", key: "media" }
                      { namespace: "custom", key: "primary_color" }
                      { namespace: "custom", key: "accent_color" }
                      { namespace: "custom", key: "store_media_card_new" }
                    ]
                  ) {
                    namespace
                    key
                    type
                    value
                    reference {
                      __typename
                      ... on MediaImage {
                        image {
                          ...image
                        }
                      }
                      ... on Video {
                        id
                        sources {
                          url
                          mimeType
                        }
                      }
                    }
                    references(first: 5) {
                      edges {
                        node {
                          __typename
                          ... on MediaImage {
                            image {
                              ...image
                            }
                          }
                          ... on Video {
                            id
                            sources {
                              url
                              mimeType
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${imageFragment}
`;

export const getProductRecommendationsQuery = /* GraphQL */ `
  query getProductRecommendations($productId: ID!) {
    productRecommendations(productId: $productId) {
      ...product
    }
  }
  ${productFragment}
`;
